import type { CodeableConcept, Coding } from '@medplum/fhirtypes'
import {
  capitalize,
  castArray,
  compact,
  findKey,
  intersection,
  uniq,
} from 'lodash-es'
import { invariant } from '#src/invariant.ts'
import type { KebabToSnake } from '#src/types.ts'
import { constantCase } from '#src/utils.ts'

// Need this to throw errors on empty objects or codeable concepts
export type CodingValue = Coding & { code: string }

export function codeableConcept(coding: null | undefined): undefined
export function codeableConcept(
  coding: CodingValue | CodingValue[]
): CodeableConcept
export function codeableConcept(
  coding: CodingValue | CodingValue[] | null | undefined
): CodeableConcept | undefined {
  if (!coding) {
    return undefined
  }
  const codings = castArray(coding)

  const text =
    codings.find((x) => x.userSelected)?.display ?? codings[0]?.display
  return {
    coding: codings,
    text,
  }
}

export const containsCoding = (
  codeableConcept: CodeableConcept,
  coding: Coding | undefined
): boolean => {
  if (!coding) {
    return false
  }
  return !!codeableConcept.coding?.some(
    (c) => c.system === coding.system && c.code === coding.code
  )
}

export const hasCodeableConcept = (
  a: CodeableConcept | CodeableConcept[] | undefined,
  b: CodeableConcept | undefined
): boolean => {
  if (!a || !b) {
    return false
  }
  const keys = (codeableConcept: CodeableConcept): string[] =>
    codeableConcept.coding?.map(
      (coding) => `${coding.system}|${coding.code}`
    ) ?? []

  const aKeys = Array.isArray(a) ? a.flatMap(keys) : keys(a)
  const bKeys = keys(b)

  return intersection(aKeys, bKeys).length > 0
}

export const hasAllCodeableConcepts = (
  a: CodeableConcept | CodeableConcept[] | undefined,
  b: CodeableConcept | CodeableConcept[] | undefined
): boolean => {
  if (!a || !b) {
    return false
  }
  const keys = (codeableConcept: CodeableConcept): string[] =>
    codeableConcept.coding?.map(
      (coding) => `${coding.system}|${coding.code}`
    ) ?? []

  const aKeys = Array.isArray(a) ? a.flatMap(keys) : keys(a)
  const bKeys = Array.isArray(b) ? b.flatMap(keys) : keys(b)

  const intersectionLeng = intersection(aKeys, bKeys).length

  return intersectionLeng === aKeys.length && intersectionLeng === bKeys.length
}

export const isSubsetOfCodeableConcepts = (
  a: CodeableConcept | CodeableConcept[] | undefined,
  b: CodeableConcept | CodeableConcept[] | undefined
): boolean => {
  if (!a || !b) {
    return false
  }
  const keys = (codeableConcept: CodeableConcept): string[] =>
    codeableConcept.coding?.map(
      (coding) => `${coding.system}|${coding.code}`
    ) ?? []

  const aKeys = Array.isArray(a) ? a.flatMap(keys) : keys(a)
  const bKeys = Array.isArray(b) ? b.flatMap(keys) : keys(b)

  return aKeys.every((key) => bKeys.includes(key))
}

export function codeableConceptHasSystem(
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined,
  system: string
): boolean {
  return (
    !!codeableConcept &&
    !!castArray(codeableConcept)
      .flatMap((c) => c.coding)
      .find((c) => c && c.system === system)
  )
}

export const getCodeableConceptBySystem = (
  concepts: CodeableConcept[] | undefined,
  system: string
): CodeableConcept | undefined =>
  concepts?.find((concept) =>
    concept.coding?.find((coding) => coding.system === system)
  )

export function getCodingFromCodeableConcept(
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined,
  system?: string
): Coding | undefined {
  return getCodingsFromCodeableConcept(codeableConcept, system)?.[0]
}

export function getCodingsFromCodeableConcept(
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined,
  system?: string
): Coding[] | undefined {
  return (
    codeableConcept &&
    compact(
      castArray(codeableConcept)
        .flatMap((c) => c.coding)
        .filter((c) => c && c.system === (system ?? c.system))
    )
  )
}

export function codeFromCodeableConcept(
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined,
  system?: string
): string | undefined {
  return getCodingFromCodeableConcept(codeableConcept, system)?.code
}
export function displayFromCodeableConcept(
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined,
  system?: string
): string | undefined {
  return getCodingFromCodeableConcept(codeableConcept, system)?.display
}

export function systemFromCodeableConcept(
  codeableConcept: CodeableConcept | undefined
): string | undefined {
  return getCodingFromCodeableConcept(codeableConcept)?.system
}

export function conceptMapFromCodes<
  T extends readonly string[],
  V extends T[number],
>({
  codes,
  system,
  displayFn = (code) =>
    code
      .split('-')
      .map((word) => capitalize(word))
      .join(' '),
}: {
  codes: T
  system: string
  displayFn?: (code: V) => string
}): { [K in V as Uppercase<KebabToSnake<K>>]: CodeableConcept } {
  return Object.fromEntries(
    uniq(codes).map((code) => [
      constantCase(code),
      codeableConcept({
        system,
        code: code.toLowerCase(),
        display: displayFn(code as V),
      }),
    ])
  ) as {
    [K in V as Uppercase<KebabToSnake<K>>]: CodeableConcept
  }
}

export function keyByCodeableConcept<T extends string>(
  codeableConceptRecord: Record<T, CodeableConcept>,
  codeableConcept: CodeableConcept | CodeableConcept[] | undefined
): T {
  const key = findKey(codeableConceptRecord, (value) =>
    hasCodeableConcept(codeableConcept, value)
  )
  invariant(key, 'Codeable concept not found')
  return key as T
}
